import { useCallback, useRef, useState } from 'react';
import { useUnmount } from 'react-use';

import { aiAgentsApi } from './aiAgents.api';

export interface UseRunAiAgentResult {
  run: () => void;
  text: string | undefined;
  generatedByAgentId: string | undefined;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}

export function useRunAiAgent(
  agentId: string,
  dixaConversationId: string,
  userId: string,
): UseRunAiAgentResult {
  const [runKey, setRunKey] = useState(0);
  const [trigger, result] = aiAgentsApi.useLazyRunAiAgentQuery();
  const triggerRef = useRef<{ abort: () => void } | null>(null);

  const run = useCallback(() => {
    triggerRef.current?.abort();
    const nextKey = runKey + 1;
    setRunKey(nextKey);
    triggerRef.current = trigger(
      { agentId, dixaConversationId, userId, runKey: nextKey },
      false,
    );
  }, [trigger, agentId, dixaConversationId, userId, runKey]);

  useUnmount(() => {
    triggerRef.current?.abort();
  });

  const data = result.data;

  return {
    run,
    generatedByAgentId: data ? result.originalArgs?.agentId : undefined,
    text: data?.text || undefined,
    isLoading:
      (data?.meta.isLoading ?? false) || (data?.meta.isStreaming ?? false),
    isError: data?.meta.isError ?? false,
    isSuccess: data?.meta.isSuccess ?? false,
  };
}
